<template>
    <div class="canvas" :class="{ type, isEmpty }">
        <div class="title" v-if="title">{{ title }}</div>

        <div v-if="!isLoading" class="inner" :class="type">
            <div v-if="isEmpty" class="w-100 h-100 text-center">No Data Available</div>

            <div v-else class="w-100 h-100">
                <div v-if="type === 'scorecard'" class="score">
                    {{ typeof chartData.result.data === "number" ? parseFloat(chartData.result.data).toFixed(2) :
                        chartData.result.data }}
                </div>

                <div v-else-if="type === 'multi-scorecard'">
                    {{ chartData }}
                </div>

                <div v-else-if="type === 'table'" class="table">
                    <div class="table-scroll">
                        <table>
                            <thead>
                                <tr>
                                    <th v-for="(thisHeader, hIndex) in tableHeaders" :key="hIndex">
                                        {{ thisHeader }}
                                    </th>
                                </tr>
                            </thead>

                            <tbody>
                                <tr v-for="(thisData, dIndex) in tableData" :key="dIndex">
                                    <td v-for="(thisHeader, hIndex) in tableHeaders" :key="hIndex">
                                        {{ thisData[thisHeader] }}
                                    </td>
                                </tr>
                            </tbody>
                        </table>

                        <pagination class="pagination-wrapper" :totalCount="chartData?.size"
                            :currentPage="currentPaginationNumber" @change="
                                (e) => {
                                    const pageNumber = e.page;
                                    currentPaginationNumber = pageNumber;
                                    $emit('change_page', pageNumber);
                                }
                            " :pageSize="20" />
                    </div>
                </div>

                <div v-else-if="isChart" class="chart">
                    <canvas :ref="id" :id="id"></canvas>
                </div>
            </div>
        </div>

        <div v-else class="inner" :class="type">
            <VProgressRing class="loader" connotation="cta" :size="-4" />
        </div>
    </div>
</template>

<script>
import Chart from "chart.js/auto";
import { SankeyController, Flow } from "chartjs-chart-sankey";
import { random_color } from "/src/helpers/global.helpers.js";
import { useUIStore } from '@/stores/ui';
import pagination from "/src/components/pagination.vue";

export default {
    name: "CustomChart",

    components: { pagination },

    props: {
        data: Object,
        title: String,
        legend: { type: Boolean, default: true },
        legendOptions: {
            type: Object,
            default: () => ({
                position: "bottom",
                align: "center",
                padding: 12,
                maxLabelLength: 20
            })
        },
        pluginsOptions: { type: Object, default: () => ({}) },
        extraPlugins: { type: Array, default: () => [] },
        legendLabelMaxLength: {
            type: Number,
            default: 20
        }
    },

    emits: ["change_page"],

    data: () => ({
        id: Date.now(),
        chart: undefined,
        currentPaginationNumber: 1,
    }),

    watch: {
        chartData: {
            handler() {
                this.fillChart();
            },
            deep: true,
        },
        isDarkMode() {
            this.fillChart();
        },
    },

    computed: {
        isDarkMode() { return useUIStore().isDarkMode; },
        chartOptions() {
            const lg = this.legendOptions || {};

            const maxLen = lg.maxLabelLength ?? 20;

            const truncate = (text) => {
                if (!text || typeof text !== "string") return text;
                if (text.length <= maxLen) return text;
                return text.slice(0, maxLen - 1) + "…";
            };

            const legendConfig = {
                display: this.legend,
                position: lg.position || "bottom",
                align: lg.align || "center",
                labels: {
                    padding: lg.padding ?? 12,
                    generateLabels: (chart) => {
                        const original = Chart.defaults.plugins.legend.labels.generateLabels(chart);

                        return original.map((item) => ({
                            ...item,
                            text: truncate(item.text),
                        }));
                    },
                },
                ...(lg || {}),
                labels: {
                    ...(lg?.labels || {}),
                    padding: lg.padding ?? lg.labels?.padding ?? 12,
                    generateLabels: (chart) =>
                        Chart.defaults.plugins.legend.labels
                            .generateLabels(chart)
                            .map((item) => ({
                                ...item,
                                text: truncate(item.text),
                            })),
                },
            };
            return {
                plugins: {
                    legend: legendConfig,
                    ...this.pluginsOptions,
                },
                layout: {
                    padding: {
                        bottom: lg.bottomPadding ?? 10,
                    }
                },
                responsive: true,
                maintainAspectRatio: false,
            };
        },
        isLoading() {
            return this.data?.loading;
        },
        chartData() {
            return this.data?.data;
        },
        type() {
            return this.chartData?.result?.chart_type;
        },

        isChart() {
            return ["line", "multiline", "bar", "doughnut", "pie", "sankey"].includes(this.type);
        },

        isEmpty() {
            return this.chartData?.size === 0;
        },

        tableHeaders() {
            return this.type === "table" ? Object.keys(this.chartData?.result?.data?.[0]) : undefined;
        },

        tableData() {
            return this.type === "table" ? this.chartData?.result?.data : undefined;
        },

        chartDataWithColors() {
            switch (this.type) {
                case "line": {
                    const c = random_color();
                    return {
                        labels: this.chartData?.result?.labels,
                        datasets: [
                            {
                                label: this.title,
                                data: this.chartData?.result?.data,
                                fill: false,
                                borderColor: c.rgb,
                                tension: 0.1,
                            },
                        ],
                    };
                }

                case "multiline":
                    return {
                        labels: this.chartData?.result?.labels,
                        datasets: this.chartData?.result?.datasets.map((e) => {
                            const c = random_color();
                            return { ...e, borderColor: c.rgb, backgroundColor: c.rgba };
                        }),
                    };

                case "doughnut":
                case "pie":
                    return {
                        labels: this.chartData?.result?.labels,
                        datasets: [
                            {
                                label: this.title,
                                data: this.chartData?.result?.data.map((e) => (typeof e === "string" && e.includes("%") ? parseFloat(e) : e)),
                                backgroundColor: this.chartData?.result?.data.map(() => random_color().rgba),
                            },
                        ],
                    };

                case "bar": {
                    const backgroundColor = [];
                    const borderColor = [];

                    this.chartData?.result?.data.forEach(() => {
                        const c = random_color();
                        backgroundColor.push(c.rgba);
                        borderColor.push(c.rgb);
                    });

                    return {
                        labels: this.chartData?.result?.labels,
                        datasets: [
                            {
                                label: this.title,
                                data: this.chartData?.result?.data,
                                backgroundColor,
                                borderColor,
                                borderWidth: 1,
                            },
                        ],
                    };
                }

                case "sankey": {
                    const colorFrom = [],
                        colorTo = [];
                    this.chartData?.result?.data.forEach(() => {
                        const c = random_color();
                        colorFrom.push(c.rgba);
                        colorTo.push(c.rgb);
                    });

                    return {
                        labels: this.chartData?.result?.labels,
                        datasets: [
                            {
                                label: this.title,
                                data: this.chartData?.result?.data,
                                colorMode: "gradient",
                                colorFrom,
                                colorTo,
                            },
                        ],
                    };
                }

                default:
                    return this.chartData?.result;
            }
        },
    },

    methods: {
        fillChart() {
            if (!this.isEmpty && this.isChart) {
                this.$nextTick(() => {
                    if (this.chart) this.chart.destroy();

                    if (this.type === "sankey") Chart.register(SankeyController, Flow);

                    /* NEW: allow plugin injection */
                    if (this.extraPlugins.length) Chart.register(...this.extraPlugins);

                    this.chart = new Chart(this.$refs[this.id], {
                        type: this.type === "multiline" ? "line" : this.type,
                        data: this.chartDataWithColors,
                        options: this.chartOptions,
                    });
                });
            }
        },
    },
};
</script>

<style scoped lang="scss">
.canvas {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-content: center;
    align-items: center;
    width: 100%;
    padding: 0 20px 20px 20px;
    border-radius: 8px;
    background-color: var(--white);

    &.scorecard {
        height: 200px;
        display: flex;
        flex-direction: column;
        justify-content: space-around;

        .score {
            font-size: 2.8rem;
        }
    }

    &.table {
        padding: 0 20px;
        max-height: 550px;
        overflow-y: auto;
        overflow-x: auto;

        &.isEmpty {
            padding: 0 20px 20px 20px;
        }
    }

    .table-scroll {
        width: 100%;
        overflow-x: auto;
    }

    .title {
        font-weight: bold;
        margin-bottom: 20px;
        background-color: var(--white);
        padding: 20px 10px;
        position: sticky;
        top: 0;
        width: 100%;
    }

    .inner {
        display: flex;
        justify-content: center;
        width: 100%;
        height: 100%;

        &.scorecard {
            padding: 10px;
        }

        .chart {
            width: 100%;
            height: 100%;

            canvas {
                width: 100%;
                height: 100%;
            }
        }

        .loader {
            margin: auto;
        }
    }

    table {
        border-collapse: collapse;
        margin: 0;
        min-width: 100%;
        table-layout: auto;
    }

    td,
    th {
        border: 1px solid #dddddd;
        text-align: left;
        padding: 8px;
        color: #333333;
    }

    td {
        color: #383a3e;
    }

    tr:nth-child(even) {}

    .pagination-wrapper {
        position: sticky;
        bottom: 0;
        background-color: white;
        padding: 10px;
    }
}

@media only screen and (max-width: 780px) {
    .canvas {
        padding: 0 10px 10px 10px;

        &.scorecard {
            height: 140px;

            .score {
                font-size: 2.2rem;
            }
        }

        &.table {
            padding: 0 10px;
            max-height: 450px;
        }

        .title {
            margin-bottom: 10px;
            padding: 10px 5px;
        }

        .inner {
            &.scorecard {
                padding: 5px;
            }

            .chart {
                canvas {}
            }

            .loader {}
        }

        table {}

        td,
        th {
            padding: 4px;
        }

        tr:nth-child(even) {}

        .pagination-wrapper {
            padding: 5px;
        }
    }
}
</style>
